import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from './Toast';

const ToastContext = createContext(null);

/**
 * Toast Container Provider
 * Manages multiple toast notifications
 */
export const ToastProvider = ({ children, position = 'top-right' }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success', duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    
    setToasts((prev) => [...prev, newToast]);

    // Auto-remove toast after duration
    setTimeout(() => {
      removeToast(id);
    }, duration + 300); // Add 300ms for exit animation

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Context value with convenience methods
  const contextValue = {
    showToast,
    success: (message, duration) => showToast(message, 'success', duration),
    error: (message, duration) => showToast(message, 'error', duration),
    warning: (message, duration) => showToast(message, 'warning', duration),
    info: (message, duration) => showToast(message, 'info', duration),
    danger: (message, duration) => showToast(message, 'danger', duration),
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Each Toast positions itself `fixed`; this wrapper only groups them.
          It used to carry a `toast-container` class whose styling came from
          the uncompiled `<style jsx>` block in Notifications/ToastNotification
          leaking into the global stylesheet — including a `pointer-events: none`
          that toasts inherited, which made their close buttons unclickable. */}
      <div>
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            style={{
              marginBottom: index < toasts.length - 1 ? '12px' : '0',
            }}
          >
            <Toast
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              position={position}
              onClose={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 * @returns {Object} Toast methods (showToast, success, error, warning, info, danger)
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;
